import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import SiteHeader from '@/components/landing/site-header'
import SiteFooter from '@/components/landing/site-footer'

export interface LegalSection {
    id: string
    heading: string
    paragraphs: string[]
    bullets?: string[]
}

interface LegalPageProps {
    seoTitle: string
    description: string
    eyebrow: string
    title: string
    tagline: string
    updated: string
    sections: LegalSection[]
}

const LegalPage: FC<LegalPageProps> = ({ seoTitle, description, eyebrow, title, tagline, updated, sections }): ReactNode => (
    <Layout>
        <Seo title={seoTitle} description={description} />

        <div className='min-h-screen bg-[#060b14] text-slate-200'>
            <SiteHeader />

            <div className='mx-auto grid max-w-6xl gap-12 px-6 pt-14 pb-20 md:pt-20 lg:grid-cols-[220px_1fr]'>
                <nav aria-label='On this page' className='hidden lg:block'>
                    <div className='sticky top-24'>
                        <p className='text-xs font-medium tracking-[0.15em] text-slate-500 uppercase'>On this page</p>
                        <ul className='mt-4 space-y-2 text-sm'>
                            {sections.map((section) => (
                                <li key={section.id}><a href={`#${section.id}`} className='text-slate-400 hover:text-cyan-300'>{section.heading}</a></li>
                            ))}
                        </ul>
                    </div>
                </nav>

                <article className='max-w-3xl'>
                    <header id='hero'>
                        <p className='font-mono text-xs tracking-[0.2em] text-cyan-300 uppercase'>{eyebrow}</p>
                        <h1 className='mt-4 text-4xl font-extrabold tracking-tight text-white md:text-5xl'>{title}</h1>
                        <p className='mt-4 text-lg text-slate-400'>{tagline}</p>
                        <p className='mt-3 text-sm text-slate-500'>Last updated {updated}</p>
                    </header>

                    <div className='mt-12 space-y-10'>
                        {sections.map((section) => (
                            <section key={section.id} id={section.id} className='scroll-mt-24'>
                                <h2 className='text-xl font-bold text-white'>{section.heading}</h2>
                                {section.paragraphs.map((paragraph) => (
                                    <p key={paragraph} className='mt-3 leading-relaxed text-slate-300'>{paragraph}</p>
                                ))}
                                {section.bullets && (
                                    <ul className='mt-3 list-disc space-y-2 pl-5 leading-relaxed text-slate-300 marker:text-cyan-300'>
                                        {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                                    </ul>
                                )}
                            </section>
                        ))}
                    </div>

                    <p className='mt-14 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-sm text-slate-400'>
                        Questions? <a href='/support#contact' className='font-semibold text-cyan-300 hover:underline'>Send us a message</a> and we will reply by email.
                    </p>
                </article>
            </div>

            <SiteFooter />
        </div>
    </Layout>
)

export default LegalPage
